import * as yup from "yup";

import { Button, ButtonProps, Flex } from "@chakra-ui/react";
import { AppForm, FormField } from "@picovico/ui";
import {
  catchErrorHandler,
  getObjectDifferenceValue,
  parseApiError,
} from "@picovico/utils";

import { useAppToast } from "@/hooks/useAppToast";
import { WhatsAppOffersToggle } from "@components/WhatsApp";
import { GENDER_OPTION } from "constants/selectOptions";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "store";
import { useUpdateUserProfileMutation } from "store/services/user";
import { updateUserProfileAction } from "store/slices";

export interface userProfileValues {
  name: string;
  email: string;
  mobile: string;
  username: string;
  location: string | null;
  date_of_birth: string | null;
  gender: string | null;
}

const userProfileValidationSchema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().email().required().label("Email"),
  //remove length in case for global validation
  mobile: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Mobile number is not valid",
    )
    .length(10, "Mobile number should be 10 digits"),
  location: yup.string().label("Address"),
  date_of_birth: yup
    .string()
    .nullable()
    .matches(
      /(((20[012]\d|19\d\d)|(1\d|2[0123]))-((0[0-9])|(1[012]))-((0[1-9])|([12][0-9])|(3[01])))/i,
      "Please enter a valid date of format YYYY-MM-DD",
    )
    .label("Date of Birth"),
});

function UserProfileForm({
  formProps,
  saveButtonProps,
  ...props
}: {
  formProps?: any;
  saveButtonProps?: ButtonProps;
  // [key: string]: BoxProps;
}) {
  const { error: errorToast, success, info } = useAppToast();
  const [updateUser, { isLoading, error }] = useUpdateUserProfileMutation({});
  const user = useAppSelector((state) => state.user.user);
  const dispatch = useDispatch();

  const userInitialValues: userProfileValues = useMemo(() => {
    return {
      name: user?.name || "",
      email: user?.email || "",
      mobile: user?.mobile || "",
      location: user?.location || "",
      username: user?.username || "",
      date_of_birth: user?.date_of_birth || null,
      gender: user?.gender || "",
    };
  }, [user]);

  return (
    <AppForm<userProfileValues>
      apiError={(error && parseApiError(error)) || null}
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        gap: "1rem",
        mt: "1.5rem",
        ...props,
      }}
      initialValues={userInitialValues}
      validationSchema={userProfileValidationSchema}
      onSubmit={async (values, { resetForm }) => {
        const data = getObjectDifferenceValue<typeof values>(
          values,
          userInitialValues,
        );
        if (!Object.keys(data).length) {
          info("Please update your profile.");
          return;
        }

        await updateUser({
          ...values,
        })
          .unwrap()
          .then((res) => {
            res?.message && success(res.message);
            dispatch(updateUserProfileAction(values));
            resetForm();
          })
          .catch((err) => {
            catchErrorHandler(err, errorToast);
          });
      }}
    >
      <Flex gap="1rem" w="full" flexDirection={{ base: "column", lg: "row" }}>
        <FormField
          name="username"
          label="Username"
          required
          isDisabled={true}
          {...formProps}
        />
        <FormField
          name="name"
          label="Name"
          required
          apierror={parseApiError(error)}
          {...formProps}
        />
      </Flex>
      <Flex gap="1rem" w="full" flexDirection={{ base: "column", lg: "row" }}>
        <FormField name="email" label="Email address" required {...formProps} />
        <FormField
          name="mobile"
          label="Mobile"
          type="tel"
          required
          isDisabled={user?.is_verified || false}
          {...formProps}
        />
      </Flex>
      <Flex gap="1rem" w="full" flexDirection={{ base: "column", lg: "row" }}>
        <FormField
          name="date_of_birth"
          label="Date of Birth"
          {...formProps}
          placeholder="Enter your date of birth"
        />
        <FormField
          name="gender"
          label="Gender"
          type="radio"
          placeholder="Select Gender"
          options={GENDER_OPTION}
          defaultValue={user?.gender || "MALE"}
          {...formProps}
        />
      </Flex>
      <FormField
        name="location"
        label="Address"
        placeholder="Enter your Address"
        {...formProps}
      />

      <Button
        isLoading={isLoading || false}
        isDisabled={isLoading || false}
        my="1rem"
        w="full"
        type="submit"
        {...saveButtonProps}
      >
        Save
      </Button>
      {/* Saved as it is switched, apart from the profile fields (whatsapp-messaging 04 §3);
          shown only when the company sends offers on WhatsApp. */}
      <WhatsAppOffersToggle />
    </AppForm>
  );
}

export default UserProfileForm;
